import { memo, useMemo } from 'react';
import classNames from 'classnames';
import Text from '@picsart/text/Text';
import Image from '@picsart/image/Image';
import Skeleton from '@picsart/skeleton/Skeleton';
import ImageFrame from '@picsart/imageframe/ImageFrame';
import { FontWeights, Radius, Typography } from '@picsart/cascade';

import useMemoSelector from 'hooks/useMemoSelector';
import { getIsDarkModeEnabled } from 'store/selectors/general';
import dynamicTestId from 'utils/common/dynamicTestId';

import { GridWrapper, CarouselWrapper } from './CardsWrappers';
import { CardsWrapperProps, RatioCardsProps } from './types';
import useStyles from './styles';

const skeletonItems = Array.from(Array(4).keys());

const RatioCards = ({ showAsCarousel, cards, onCardSelect, selectedCard }: RatioCardsProps) => {
  const isDarkModeEnabled = useMemoSelector(getIsDarkModeEnabled);
  const classes = useStyles({ isDarkMode: isDarkModeEnabled });
  const Wrapper = useMemo(() => showAsCarousel
    ? CarouselWrapper
    : ({ children }: CardsWrapperProps) => <GridWrapper>{children}</GridWrapper>,
  [showAsCarousel]);

  return (
    <Wrapper>
      {cards.length ? cards.map(card => (
        <ImageFrame
          dataTestId={dynamicTestId('ratio', card.id)}
          className={classNames(classes.imageFrame, { [classes.active]: card.id === selectedCard?.id })}
          isActive={card.id === selectedCard?.id}
          onClick={() => onCardSelect(card)}
          borderRadius={Radius.r4}
          key={card.id}
          width={showAsCarousel ? 65 : '100%'}
        >
          <Image className={classes.image} width={35} height={35} src={card.preview_small_url || card.preview_url} alt={card.name} />
          <Text size={Typography.t3} isFullWidth maxVisibleLinesCount={1} weight={FontWeights.SemiBold}>{card.name}</Text>
        </ImageFrame>
      )) : skeletonItems.map(x => <Skeleton key={x} height={70} width={70} borderRadius={Radius.r4} />)}
    </Wrapper>
  );
};

export default memo(RatioCards);
